/**
 * Agents service with intelligent cache strategy
 * Implementação conforme ADR-004: Context + localStorage + Background Refresh
 */

import api, { apiEndpoints } from '../lib/api'
import { STORAGE_KEYS } from '../lib/storageKeys'
import type { Agent, AgentUI, CacheMetadata } from '../types/agents'

class AgentsService {
  private readonly CACHE_KEY = STORAGE_KEYS.AGENTS_CACHE
  private readonly CACHE_TTL = 24 * 60 * 60 * 1000 // 24 horas
  private readonly BACKGROUND_REFRESH_THRESHOLD = 60 * 60 * 1000 // 1 hora

  /**
   * Fetch agents from API
   */
  async fetchAgents(): Promise<Agent[]> {
    try {
      const response = await api.get<Agent[]>(apiEndpoints.agents.available)
      return response.data
    } catch {
      /* Silent fail */
      throw new Error('Falha ao buscar agentes disponíveis')
    }
  }

  /**
   * Map backend Agent to frontend AgentUI
   */
  mapAgentToUI(agent: Agent): AgentUI {
    return {
      id: agent.code,
      name: agent.display_name,
      description: agent.description,
      type: agent.code,
      status: agent.is_active ? 'active' : 'inactive',
      createdAt: agent.created_at,
      updatedAt: agent.updated_at,
      lastSyncAt: agent.last_sync_at,
    }
  }

  /**
   * Get agents with cache strategy
   * 1. Try cache first (instant UX)
   * 2. Background refresh if stale
   * 3. Return cached or fresh data
   */
  async getAgents(userPermissions: string[] = []): Promise<AgentUI[]> {
    // 1. Try cache first
    const cached = this.getCachedAgents(userPermissions)
    if (cached) {
      // Background refresh if cache is old
      if (this.isCacheStale()) {
        this.refreshCache(userPermissions)
      }
      return cached
    }

    // 2. Fetch from API if no cache
    return this.refreshAgents(userPermissions)
  }

  /**
   * Force refresh from API
   */
  async refreshAgents(userPermissions: string[] = []): Promise<AgentUI[]> {
    const agents = await this.fetchAgents()

    this.setCachedAgents(agents, userPermissions)
    const mappedAgents = agents.map(agent => this.mapAgentToUI(agent))

    return mappedAgents
  }

  /**
   * Background refresh without blocking UI
   */
  private async refreshCache(userPermissions: string[]): Promise<void> {
    try {
      const agents = await this.fetchAgents()
      this.setCachedAgents(agents, userPermissions)
    } catch {
      /* Silent fail */
    }
  }

  /**
   * Get cached agents if still valid
   */
  private getCachedAgents(userPermissions: string[]): AgentUI[] | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY)
      if (!cached) return null

      const cacheData: CacheMetadata = JSON.parse(cached)

      // Check if cache is still valid
      if (this.shouldInvalidateCache(cacheData, userPermissions)) {
        this.clearCache()
        return null
      }

      return cacheData.data.map(agent => this.mapAgentToUI(agent))
    } catch {
      /* Silent fail */
      this.clearCache()
      return null
    }
  }

  /**
   * Cache agents data with metadata
   */
  private setCachedAgents(agents: Agent[], userPermissions: string[]): void {
    try {
      const cacheData: CacheMetadata = {
        data: agents,
        timestamp: Date.now(),
        userPermissions: [...userPermissions].sort(), // Sort for consistent comparison
        version: import.meta.env.VITE_APP_VERSION || '1.0.0',
      }

      localStorage.setItem(this.CACHE_KEY, JSON.stringify(cacheData))
    } catch {
      /* Silent fail */
    }
  }

  /**
   * Check if cache should be invalidated
   */
  private shouldInvalidateCache(
    cached: CacheMetadata,
    currentPermissions: string[]
  ): boolean {
    // Age check (24h TTL)
    if (Date.now() - cached.timestamp > this.CACHE_TTL) return true

    // Permissions changed
    const sortedCurrent = [...currentPermissions].sort()
    if (
      JSON.stringify(cached.userPermissions) !== JSON.stringify(sortedCurrent)
    )
      return true

    // Version changed (deploy)
    const currentVersion = import.meta.env.VITE_APP_VERSION || '1.0.0'
    if (cached.version !== currentVersion) return true

    return false
  }

  /**
   * Check if cache is stale (needs background refresh)
   */
  private isCacheStale(): boolean {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY)
      if (!cached) return true

      const cacheData: CacheMetadata = JSON.parse(cached)
      return (
        Date.now() - cacheData.timestamp > this.BACKGROUND_REFRESH_THRESHOLD
      )
    } catch {
      /* Silent fail */
      return true
    }
  }

  /**
   * Clear agents cache
   */
  clearCache(): void {
    localStorage.removeItem(this.CACHE_KEY)
  }

  /**
   * Get cache metadata for debugging
   */
  getCacheInfo(): {
    exists: boolean
    age?: number
    permissions?: string[]
  } | null {
    try {
      const cached = localStorage.getItem(this.CACHE_KEY)
      if (!cached) return { exists: false }

      const cacheData: CacheMetadata = JSON.parse(cached)
      return {
        exists: true,
        age: Date.now() - cacheData.timestamp,
        permissions: cacheData.userPermissions,
      }
    } catch {
      /* Silent fail */
      return null
    }
  }
}

export const agentsService = new AgentsService()
export default agentsService
